import stylesheet from './Textarea.scss?inline';
import {NXTInputField} from './InputField';

export class NXTTextarea extends NXTInputField {
    textarea: HTMLTextAreaElement;
    private counter?: HTMLElement;

    constructor() {
        super();
        this.customStyle = stylesheet;
    }

    get variant(): string {
        return this.getAttribute('variant') ?? 'default';
    }

    get size(): string {
        return this.getAttribute('size') ?? 'md';
    }

    get maxlength(): number | null {
        const raw = this.getAttribute('maxlength');
        return raw !== null ? Number(raw) : null;
    }

    public get value(): string {
        return this.textarea?.value;
    }

    public set value(value: string) {
        this.textarea.value = value;
        this.updateCounter();
    }

    public clear() {
        this.value = '';
    }

    getSizeVariants(): string[] {
        const additionalClasses: string[] = [];
        additionalClasses.push(`textarea-${this.size}`);
        for (const breakpoint of ['md', 'lg', 'xl', 'xxl']) {
            const attributeName = `size-${breakpoint}`;
            if (this.hasAttribute(attributeName)) {
                additionalClasses.push(`textarea-${this.getAttribute(attributeName)}-${breakpoint}-up`);
            }
        }
        return additionalClasses;
    }

    protected renderLabel(): HTMLElement {
        const hasLabel = !!this.querySelector('[slot="label"]');
        const hasCounter = this.maxlength !== null;
        if (!hasLabel && !hasCounter) {
            return super.renderLabel();
        }

        const row = document.createElement('div');
        row.classList.add('textarea-label-row');
        row.append(super.renderLabel());

        if (hasCounter) {
            this.counter = document.createElement('span');
            this.counter.classList.add('textarea-counter');
            row.appendChild(this.counter);
        }

        return row;
    }

    protected renderControl(): HTMLElement {
        const container = document.createElement('div');
        container.classList.add('textarea', `variant-${this.variant}`, ...this.getSizeVariants());

        const slottedAreas = this.getSlotElements('input').filter(
            (el): el is HTMLTextAreaElement => el instanceof HTMLTextAreaElement,
        );

        if (slottedAreas.length > 0) {
            this.textarea = slottedAreas[0];
        } else {
            const textarea = document.createElement('textarea');
            if (this.hasAttribute('value')) {
                textarea.value = this.getAttribute('value');
            }
            if (this.hasAttribute('placeholder')) {
                textarea.placeholder = this.getAttribute('placeholder');
            }
            if (this.hasAttribute('rows')) {
                textarea.setAttribute('rows', this.getAttribute('rows'));
            }
            textarea.setAttribute('slot', 'input');
            this.appendChild(textarea);
            this.textarea = textarea;
        }

        if (this.maxlength !== null) {
            this.textarea.setAttribute('maxlength', String(this.maxlength));
        }

        const slot = document.createElement('slot');
        slot.setAttribute('name', 'input');
        container.appendChild(slot);

        this.wireInput(this.textarea);
        this.updateCounter();

        return container;
    }

    private wireInput(textarea: HTMLTextAreaElement) {
        textarea.addEventListener('blur', () => {
            textarea.classList.toggle('filled', !!textarea.value);
        });
        textarea.addEventListener('change', (ev) => {
            this.dispatchEvent(new Event(ev.type, ev));
        });
        textarea.addEventListener('input', () => this.updateCounter());
    }

    private updateCounter() {
        if (!this.counter || this.maxlength === null) {
            return;
        }
        this.counter.textContent = `${this.value?.length ?? 0}/${this.maxlength}`;
    }
}

customElements.define('nxt-textarea', NXTTextarea);
